(() => {
  const DEFAULT_CONFIG = {
    apiVersion: "2026-01",
    cartAttributeName: "_provider_id",
    cardSectionId: "provider-affiliate-card",
    metaobjectType: "provider_affiliates",
    storageKey: "xomd_provider_affiliate",
    storefrontToken: ""
  };

  const config = {
    ...DEFAULT_CONFIG,
    ...(window.providerAffiliates || {})
  };

  const log = (message, payload) => {
    console.log(`[Provider Affiliates] ${message}`, payload || "");
  };

  const warn = (message, payload) => {
    console.warn(`[Provider Affiliates] ${message}`, payload || "");
  };

  const normalizeHandle = (value) => {
    if (typeof value !== "string") return "";
    return value.trim().toLowerCase();
  };

  const isResetRequested = (params) => {
    const value = normalizeHandle(params.get("provider_reset"));
    return value === "1" || value === "true";
  };

  const removeProviderParam = () => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("provider")) return;

    url.searchParams.delete("provider");
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  };

  const removeResetParam = () => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("provider_reset")) return;

    url.searchParams.delete("provider_reset");
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  };

  const getMainProviderId = () => {
    return normalizeHandle(document.querySelector("main[data-provider-id]")?.dataset.providerId || "");
  };

  const getCustomerProviderId = () => {
    return normalizeHandle(document.querySelector("main[data-customer-provider-id]")?.dataset.customerProviderId || "");
  };

  const setMainProviderId = (handle) => {
    const main = document.querySelector("main");
    if (!main) return;

    if (handle) {
      main.dataset.providerId = handle;
    } else {
      delete main.dataset.providerId;
      main.removeAttribute("data-provider-id");
    }
  };

  const removeProviderCards = () => {
    document.querySelectorAll("[data-provider-affiliate-card]").forEach((card) => card.remove());
  };

  const renderProviderCards = async () => {
    const anchors = document.querySelectorAll("[data-provider-affiliate-pdp-anchor]");
    if (!anchors.length) return;

    const url = new URL(window.location.href);
    url.search = "";
    url.searchParams.set("section_id", config.cardSectionId);

    const response = await fetch(`${url.pathname}${url.search}`, {
      credentials: "same-origin",
      cache: "no-store"
    });

    const html = await response.text();

    if (!response.ok) {
      throw new Error(`Provider card section failed with HTTP ${response.status}`);
    }

    anchors.forEach((anchor) => {
      anchor.innerHTML = html.trim();
    });
  };

  const getStoredProvider = () => {
    try {
      const raw = window.localStorage.getItem(config.storageKey);
      if (!raw) return "";

      const parsed = JSON.parse(raw);
      if (typeof parsed === "string") return normalizeHandle(parsed);
      return normalizeHandle(parsed?.handle || "");
    } catch (error) {
      warn("Unable to read stored provider.", error);
      return "";
    }
  };

  const setStoredProvider = (handle) => {
    try {
      window.localStorage.setItem(
        config.storageKey,
        JSON.stringify({
          handle,
          updatedAt: new Date().toISOString()
        })
      );
    } catch (error) {
      warn("Unable to store provider.", error);
    }
  };

  const clearStoredProvider = () => {
    try {
      window.localStorage.removeItem(config.storageKey);
    } catch (error) {
      warn("Unable to clear stored provider.", error);
    }
  };

  const updateCartProvider = async (handle) => {
    const response = await fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      credentials: "same-origin",
      body: JSON.stringify({
        attributes: {
          [config.cartAttributeName]: handle || ""
        }
      })
    });

    const cart = await response.json();

    if (!response.ok) {
      throw new Error(`Cart update failed with HTTP ${response.status}`);
    }

    return cart;
  };

  const validateProvider = async (handle) => {
    if (!config.storefrontToken) {
      warn("Missing Storefront API token.");
      return null;
    }

    const endpoint = `${window.location.origin}/api/${config.apiVersion}/graphql.json`;
    const query = `
      query ProviderAffiliate($handle: MetaobjectHandleInput!) {
        metaobject(handle: $handle) {
          id
          handle
          type
          fields {
            key
            value
          }
        }
      }
    `;

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": config.storefrontToken
      },
      body: JSON.stringify({
        query,
        variables: {
          handle: {
            type: config.metaobjectType,
            handle
          }
        }
      })
    });

    const json = await response.json();

    if (!response.ok) {
      throw new Error(`Storefront validation failed with HTTP ${response.status}`);
    }

    if (json.errors?.length) {
      throw new Error(json.errors.map((error) => error.message).join("; "));
    }

    const metaobject = json.data?.metaobject || null;
    return metaobject?.handle === handle && metaobject?.type === config.metaobjectType ? metaobject : null;
  };

  const applyProvider = async (handle, source) => {
    const metaobject = await validateProvider(handle);

    if (!metaobject) {
      if (source === "url") {
        removeProviderParam();
      }

      log("Validation result", {
        handle,
        source,
        valid: false
      });
      return false;
    }

    const normalizedHandle = normalizeHandle(metaobject.handle);
    await updateCartProvider(normalizedHandle);
    setStoredProvider(normalizedHandle);
    setMainProviderId(normalizedHandle);

    try {
      await renderProviderCards();
    } catch (error) {
      warn("Unable to render provider card.", error);
    }

    log("Validation result", {
      handle: normalizedHandle,
      fields: metaobject.fields || [],
      metaobjectId: metaobject.id,
      source,
      valid: true
    });

    return true;
  };

  const resetProvider = async (reason) => {
    clearStoredProvider();
    setMainProviderId("");
    removeProviderCards();
    await updateCartProvider("");
    removeResetParam();
    log("Provider reset", { reason });
  };

  const init = async () => {
    const params = new URLSearchParams(window.location.search);

    try {
      if (isResetRequested(params)) {
        await resetProvider("provider_reset");
        return;
      }

      const urlProvider = normalizeHandle(params.get("provider"));
      if (urlProvider) {
        await applyProvider(urlProvider, "url");
        return;
      }

      const cartProvider = getMainProviderId();
      if (cartProvider) {
        setStoredProvider(cartProvider);
        log("Validation result", {
          handle: cartProvider,
          source: "cart_attribute",
          valid: true,
          validated: false
        });
        return;
      }

      const customerProvider = getCustomerProviderId();
      if (customerProvider) {
        await applyProvider(customerProvider, "customer_tag");
        return;
      }

      const storedProvider = getStoredProvider();
      if (!storedProvider) return;

      const applied = await applyProvider(storedProvider, "local_storage");
      if (!applied) {
        await resetProvider("invalid_local_storage");
      }
    } catch (error) {
      warn("Provider validation failed.", error);
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
